/**
 * Meal Plan API utilities
 * Handles communication with backend for weekly meal planning
 */

import { API_URL } from '../constants';

/**
 * Get Monday of a given week (or current week if no date provided)
 */
export function getWeekStart(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
}

/**
 * Get array of dates for a week starting from weekStart
 */
export function getWeekDates(weekStart) {
  const dates = [];
  const start = new Date(weekStart);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}

/**
 * Format date for display (e.g., "Mon 9")
 */
export function formatDayShort(dateStr) {
  const date = new Date(dateStr);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${days[date.getDay()]} ${date.getDate()}`;
}

/**
 * Check if a date is today
 */
export function isToday(dateStr) {
  const today = new Date().toISOString().split('T')[0];
  return dateStr === today;
}

/**
 * Check if a date is in the past
 */
export function isPast(dateStr) {
  const today = new Date().toISOString().split('T')[0];
  return dateStr < today;
}

/**
 * Get meal plans from backend
 * @param {string} accessToken - Auth token
 * @param {number} weeks - Number of weeks to fetch (default 2)
 */
export async function getMealPlans(accessToken, weeks = 2) {
  const response = await fetch(`${API_URL}/api/meal-plans?weeks=${weeks}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch meal plans');
  }

  return response.json();
}

/**
 * Get a specific week's meal plan
 * @param {string} weekStart - Week start date (YYYY-MM-DD)
 * @param {string} accessToken - Auth token
 */
export async function getMealPlan(weekStart, accessToken) {
  const response = await fetch(`${API_URL}/api/meal-plans/${weekStart}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to fetch meal plan');
  }

  return response.json();
}

/**
 * Save a week's meal plan
 * @param {string} weekStart - Week start date (YYYY-MM-DD)
 * @param {array} meals - Array of meal objects
 * @param {string} accessToken - Auth token
 */
export async function saveMealPlan(weekStart, meals, accessToken) {
  const response = await fetch(`${API_URL}/api/meal-plans/${weekStart}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ meals })
  });

  if (!response.ok) {
    throw new Error('Failed to save meal plan');
  }

  return response.json();
}

/**
 * Delete a week's meal plan
 * @param {string} weekStart - Week start date (YYYY-MM-DD)
 * @param {string} accessToken - Auth token
 */
export async function deleteMealPlan(weekStart, accessToken) {
  const response = await fetch(`${API_URL}/api/meal-plans/${weekStart}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    throw new Error('Failed to delete meal plan');
  }

  return response.json();
}

/**
 * Generate shopping list from meal plan
 * Compares required ingredients against pantry
 * @param {array} meals - Planned meals with ingredients
 * @param {array} pantryItems - Current pantry inventory
 * @returns {array} Items needed (not in pantry)
 */
export function generateShoppingList(meals, pantryItems) {
  // Collect all ingredients from planned meals
  const neededIngredients = new Map(); // name -> { quantity, linkedMeals }

  for (const meal of meals) {
    if (meal.status === 'skipped' || !meal.ingredients) continue;

    for (const ingredient of meal.ingredients) {
      const name = ingredient.toLowerCase().trim();
      if (neededIngredients.has(name)) {
        neededIngredients.get(name).linkedMeals.push(meal.date);
      } else {
        neededIngredients.set(name, {
          name: ingredient,
          linkedMeals: [meal.date],
          source: 'meal-plan'
        });
      }
    }
  }

  // Filter out items already in pantry
  const pantryNames = new Set(
    pantryItems.map(item => item.name.toLowerCase().trim())
  );

  const shoppingItems = [];
  for (const [name, item] of neededIngredients) {
    if (!pantryNames.has(name)) {
      shoppingItems.push(item);
    }
  }

  return shoppingItems;
}
